import {
  PlayerColor, TokenState, DiceValue, TokenPosition, PlayerState,
  PATH_LENGTH, STRETCH_LENGTH, TOKENS_PER_PLAYER,
  PLAYER_START_POSITIONS, PLAYER_HOME_STRETCH_ENTRY,
  SAFE_POSITIONS, CONSECUTIVE_SIX_LIMIT,
} from './types';

export class RuleEngine {
  canTokenMove(
    token: TokenPosition,
    diceValue: DiceValue,
    playerColor: PlayerColor,
    allPlayers: PlayerState[],
  ): boolean {
    if (token.state === TokenState.FINISHED) return false;

    if (token.state === TokenState.HOME) {
      return diceValue === 6;
    }

    if (token.state === TokenState.ON_PATH) {
      const start = PLAYER_START_POSITIONS[playerColor];
      const entry = PLAYER_HOME_STRETCH_ENTRY[playerColor];
      let distanceToStretch: number;

      if (entry >= start) {
        distanceToStretch = entry - start;
      } else {
        distanceToStretch = PATH_LENGTH - start + entry;
      }

      const stepsTaken = this.getStepsTaken(token.pathIndex, start);
      const remainingToStretch = distanceToStretch - stepsTaken;

      if (diceValue <= remainingToStretch) {
        return true;
      }

      const stretchSteps = stepsTaken + diceValue - (distanceToStretch + 1);
      if (stretchSteps >= 0 && stretchSteps < STRETCH_LENGTH) {
        return true;
      }
    }

    if (token.state === TokenState.HOME_STRETCH) {
      const finalStep = STRETCH_LENGTH - 1;
      // Exact roll needed to land on the last stretch cell then one more exact
      // roll to finish — no overshoot into the center.
      if (token.stretchIndex === finalStep) return false;
      const stepsRemaining = finalStep - token.stretchIndex;
      return diceValue <= stepsRemaining;
    }

    return false;
  }

  private getStepsTaken(currentPathIndex: number, startIndex: number): number {
    if (currentPathIndex >= startIndex) {
      return currentPathIndex - startIndex;
    }
    return PATH_LENGTH - startIndex + currentPathIndex;
  }

  getValidMoves(
    playerIndex: number,
    diceValue: DiceValue,
    players: PlayerState[],
  ): number[] {
    const valid: number[] = [];
    const player = players[playerIndex];

    for (let i = 0; i < TOKENS_PER_PLAYER; i++) {
      const token = player.tokens[i];
      if (this.canTokenMove(token, diceValue, player.config.color, players)) {
        valid.push(i);
      }
    }

    return valid;
  }

  calculateNewPosition(
    token: TokenPosition,
    diceValue: DiceValue,
    playerColor: PlayerColor,
  ): TokenPosition {
    if (token.state === TokenState.HOME) {
      return {
        state: TokenState.ON_PATH,
        pathIndex: PLAYER_START_POSITIONS[playerColor],
        stretchIndex: -1,
      };
    }

    if (token.state === TokenState.ON_PATH) {
      const start = PLAYER_START_POSITIONS[playerColor];
      const entry = PLAYER_HOME_STRETCH_ENTRY[playerColor];
      let distanceToEntry: number;

      if (entry >= start) {
        distanceToEntry = entry - start;
      } else {
        distanceToEntry = PATH_LENGTH - start + entry;
      }

      const stepsTaken = this.getStepsTaken(token.pathIndex, start);
      const newSteps = stepsTaken + diceValue;

      if (newSteps <= distanceToEntry) {
        const newPathIndex = (start + newSteps) % PATH_LENGTH;
        return {
          state: TokenState.ON_PATH,
          pathIndex: newPathIndex,
          stretchIndex: -1,
        };
      }

      const stretchSteps = newSteps - distanceToEntry - 1;
      return {
        state: TokenState.HOME_STRETCH,
        pathIndex: -1,
        stretchIndex: stretchSteps,
      };
    }

    if (token.state === TokenState.HOME_STRETCH) {
      const newStretchIndex = token.stretchIndex + diceValue;
      if (newStretchIndex === STRETCH_LENGTH - 1) {
        return {
          state: TokenState.FINISHED,
          pathIndex: -1,
          stretchIndex: -1,
        };
      }
      return {
        state: TokenState.HOME_STRETCH,
        pathIndex: -1,
        stretchIndex: newStretchIndex,
      };
    }

    return { ...token };
  }

  checkCapture(
    tokenPos: TokenPosition,
    playerColor: PlayerColor,
    players: PlayerState[],
  ): number | null {
    if (tokenPos.state !== TokenState.ON_PATH) return null;
    // Star cells and spawn (start) cells are safe — nobody is eaten there.
    if (SAFE_POSITIONS.includes(tokenPos.pathIndex)) return null;

    // Any opponent goti standing on a normal cell gets eaten — even a
    // whole stack of them. Applies to humans and AI alike.
    for (let p = 0; p < players.length; p++) {
      if (players[p].config.color === playerColor) continue;

      for (let t = 0; t < TOKENS_PER_PLAYER; t++) {
        const other = players[p].tokens[t];
        if (other.state === TokenState.ON_PATH && other.pathIndex === tokenPos.pathIndex) {
          return p;
        }
      }
    }

    return null;
  }

  sendTokenHome(token: TokenPosition): TokenPosition {
    return {
      state: TokenState.HOME,
      pathIndex: -1,
      stretchIndex: -1,
    };
  }

  hasExtraTurn(diceValue: DiceValue, consecutiveSixes: number): boolean {
    if (diceValue === 6 && consecutiveSixes < CONSECUTIVE_SIX_LIMIT) {
      return true;
    }
    return false;
  }

  isThreeSixes(consecutiveSixes: number): boolean {
    return consecutiveSixes >= CONSECUTIVE_SIX_LIMIT;
  }

  checkWin(players: PlayerState[]): number | null {
    for (let i = 0; i < players.length; i++) {
      if (players[i].finishedCount === TOKENS_PER_PLAYER) {
        return i;
      }
    }
    return null;
  }
}
